<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useForm } from "vee-validate";
import { useErrors } from "@/utils/api-functions";

//@ts-ignore
import FormInput from "@/components/form/input.vue";
//@ts-ignore
import FormCheckbox from "@/components/form/checkbox.vue";
//@ts-ignore
import FormCombobox from "@/components/form/combobox.vue";
//@ts-ignore
import Submit from "@/components/form/submit.vue";
import * as Yup from "yup";
import { nl } from "yup-locales";
import { useApiStore } from "@/stores/api";
import { useAppStore } from "@/stores/app";

const appStore = useAppStore();
const apiErrors = useErrors();
const apiStore = useApiStore();

const props = defineProps<{ groups: any; tags: any; mailchimpGroupId: string }>();

const success = ref(false);
const loading = ref(false);
const component = ref<HTMLDivElement>();

Yup.setLocale(nl);

const slugify = (str: string) =>
    str
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");

const localProjects = computed(() => {
    return props.groups.map((p: any, index: number) => {
        return {
            id: p.name,
            name: p.name,
            slug: slugify(p.name),
            mailchimp_id: p.interest_group,
        };
    });
});

const { handleSubmit, isSubmitting, values } = useForm({
    initialValues: {
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        projects: [],
        mailchimp_ids: [],
        tags: props.tags,
        terms: undefined,
        href: window.location.href,
        mailchimp_group_id: props.mailchimpGroupId,
        pathname: window.location.pathname,
    },
    validationSchema: Yup.object({
        first_name: Yup.string().required().label("Voornaam"),
        last_name: Yup.string().required().label("Achternaam"),
        phone: Yup.string()
            .matches(/(((\+|00)3[12][ ]?(?:\(0\)[ ]?)?)|0){1}(4(60|[789]\d)\/?(\s?\d{2}\.?){2}(\s?\d{2})|(\d\/?\s?\d{3}|\d{2}\/?\s?\d{2})(\.?\s?\d{2}){2})/, "Telefoonnummer is ongeldig")
            .required()
            .label("Telefoonnummer"),
        email: Yup.string().email().required().label("Email"),
        projects: Yup.array().required().label("Projecten"),
        mailchimp_ids: Yup.array().label("Mailchimp ids"),
        tags: Yup.string().label("Tags"),
        terms: Yup.boolean().required("Je dient te bevestigen dat Laud deze gegevens mag gebruiken om je aanvraag te verwerken."),
        href: Yup.string().required(),
        mailchimp_group_id: Yup.string().label("Mailchimp group ids"),
        pathname: Yup.string().required(),
    }),
});

const onSubmit = handleSubmit(async (values, { resetForm }) => {
    try {
        loading.value = true;
        values.mailchimp_ids = [...localProjects.value.filter((p: { id: string; mailchimp_id?: string }) => p.mailchimp_id && values!.projects!.includes(p.id)).map((p: any) => p.mailchimp_id)];

        // @ts-ignore
        await apiStore.keepMePosted(values);

        // @ts-ignore
        if (window.hasOwnProperty("dataLayer") && dataLayer) {
            // @ts-ignore
            dataLayer.push({
                event: "cf7submission",
                formId: "1705",
                response: [
                    { name: "first_name", value: values.first_name },
                    { name: "last_name", value: values.last_name },
                    { name: "phone", value: values.phone },
                    { name: "email", value: values.email },
                    { name: "href", value: window.location.href },
                    { name: "pathname", value: window.location.pathname },
                ],
            });
        }

        success.value = true;

        setTimeout(resetForm, 500);
    } catch (error) {
        apiErrors.parse(error);
    } finally {
        loading.value = false;
    }
});

watch(
    () => appStore.project,
    (newValue: string | null) => {
        setProject(newValue);
    },
);

const setProject = (slug?: string | null) => {
    if (!slug) {
        return;
    }

    const match = localProjects.value.find((p: any) => p.slug === slug);
    if (!match) {
        return;
    }

    if (values.projects && values.projects.includes(match.name)) {
        return;
    }

    if (values.projects && values.projects.length > 0) {
        values.projects.push(match.name);
        return;
    }

    values.projects = [match.name];
};

const urlParams = new URLSearchParams(window.location.search);
setProject(urlParams.get("project"));
</script>

<template>
    <form id="appointment_form" key="form" v-if="!success" @submit="onSubmit" class="grid">
        <component :is="apiErrors.render()" :data="apiErrors.get()"></component>

        <form-input name="first_name" label-position="left" type="text" label="Voornaam"></form-input>
        <form-input name="last_name" label-position="left" type="text" label="Achternaam"></form-input>
        <form-input name="phone" label-position="left" type="text" label="Telefoonnummer"></form-input>
        <form-input name="email" label-position="left" type="email" label="Email"></form-input>

        <form-combobox input-class="mt-15" label="Hou me op de hoogte voor volgend(e) project(en)" name="projects" :options="localProjects" class="w-full my-30"></form-combobox>

        <form-checkbox
            :value="false"
            class="mb-[23px] text-[10px] leading-[15px]"
            name="terms"
            :hide-box="true"
            label="Ik ga ermee akkoord dat mijn in het contactformulier verstrekte gegevens door LAUD als verantwoordelijke instantie worden verzameld, verwerkt en gebruikt voor het versturen van informatiemateriaal per post en voor het per e-mail beantwoorden van mijn aanvraag."
        ></form-checkbox>

        <div>
            <submit :disabled="success" :loading="isSubmitting || loading">Verstuur</submit>
        </div>
    </form>

    <div key="success" class="flex items-center justify-center flex-1 w-full text-white" v-else>
        <p class="text-center">Yes! Je bericht is luid en duidelijk ontvangen. Check je mailbox, we hebben je net al wat info gestuurd.</p>
    </div>
</template>
